import { type MutableRefObject, type Ref, type RefCallback } from 'react';
import {
    type FieldValues,
    type RegisterOptions,
    type Validate,
} from 'react-hook-form';

export type FieldValidate =
    // eslint-disable-next-line typescript/no-explicit-any -- Validate<T, F>'s generics are in parameter (contravariant) position; unknown would reject concretely-typed validators from real callers.
    | Validate<any, any>
    // eslint-disable-next-line typescript/no-explicit-any -- same as above, for the per-field record variant.
    | Record<string, Validate<any, any>>;

export type FieldLengthRule = number | { value: number; message: string };

export function lengthRuleValue(
    rule: FieldLengthRule | undefined,
): number | undefined {
    if (rule === undefined) {
        return undefined;
    }

    return typeof rule === 'number' ? rule : rule.value;
}

interface BuildRhfRulesOptions {
    required?: boolean;
    bulkMode?: boolean;
    validate?: FieldValidate;
    minLength?: FieldLengthRule;
    maxLength?: FieldLengthRule;
}

export function buildRhfRules({
    required,
    bulkMode,
    validate,
    minLength,
    maxLength,
}: BuildRhfRulesOptions): RegisterOptions<FieldValues, string> {
    return {
        required: required && !bulkMode,
        validate,
        minLength,
        maxLength,
    };
}

export function mergeRefs<T>(
    ...refs: Array<Ref<T> | undefined>
): RefCallback<T> {
    return (instance) => {
        refs.forEach((ref) => {
            if (typeof ref === 'function') {
                ref(instance);
                return;
            }

            if (ref) {
                (ref as MutableRefObject<T | null>).current = instance;
            }
        });
    };
}

interface RegisterSpreadWarningOptions {
    componentName: string;
    isRHFControlled: boolean;
    onChange?: unknown;
    ref?: Ref<unknown>;
}

export function warnAboutRegisterSpread({
    componentName,
    isRHFControlled,
    onChange,
    ref,
}: RegisterSpreadWarningOptions): void {
    if (
        process.env.NODE_ENV !== 'development' ||
        !isRHFControlled ||
        !ref ||
        !onChange
    ) {
        return;
    }

    console.error(
        `Do not spread register() onto <${componentName}> when using name. Pass name only and let Guardian manage React Hook Form.`,
    );
}
